/**
 * Island builder — the anchors of the endless line.
 *
 * The path planner has already committed an anchored run and claimed a mass
 * envelope (`isle/${id}` bands: per-cell free depth below the top surface,
 * pre-shrunk around older track that crosses under). This builder realizes
 * that claim as a cliff-block voxel mass with a biome on top:
 *
 * - mass: nature-kit cliff_block family (1×1×1 blocks + 0.5/0.25 slabs) on
 *   our exact grid; columns under the run always exist, blob rings around it
 *   thin out and shallow out with distance (stepped underside taper — the
 *   floating-island silhouette).
 * - biome: data-driven family table (material, cap tiles, decor rules,
 *   density). v1 rows draw on the nature kit; lava/snow/city are future rows
 *   pointing at future packs.
 * - decor: the M2a field API over the island's top — region = island bbox,
 *   oracle = the anchored run's cells, placements filtered to real mass tops
 *   and lifted to topY. Per-cell RNG children keep it order-independent.
 *
 * Every placement belongs to the island's ref; culling an island releases
 * its claim and its render group together.
 */
import type { Placement } from "../../shared/types.ts";
import type { Rng } from "../rng.ts";
import {
  cellSetOracle,
  type DecorCatalog,
  placeDecor,
  regionAround,
  settingByName,
} from "../decor.ts";
import type { StyleField } from "../style.ts";
import { Occupancy } from "./occupancy.ts";
import type { IslandCrossing } from "./path.ts";
import { TRACK_SCALE, worldCellsOf } from "./scale.ts";

export interface BiomeSpec {
  name: string;
  weight: number;
  /** Cliff family material suffix. */
  material: "rock" | "stone";
  /** Cap tile on non-track top cells (FULLY-QUALIFIED pieceId, e.g.
   * "nature-kit:ground_grass"), or bare block tops. Only the nature
   * ground tile is a clean 1×1 — themed biomes reuse it and the client
   * tints it per biome (snow, sand). */
  capId: string | null;
  capChance: number;
  /** SETTINGS row the top decor borrows, or null for barren tops. */
  setting: string | null;
  decorDensity: number;
  /** Landmark vocabulary (fully-qualified ids), one per island at most.
   * Omitted = the default nature statue/rock list. */
  landmarks?: readonly string[];
  /** Uniform scale on landmark placements (pack size normalization);
   * omitted = unscaled, byte-identical to the pre-M18a canon. */
  landmarkScale?: number;
  /** Island MASS FAMILY (user directive: other packs as other island
   * types, not just color phenotypes). Omitted = "cliff" (the nature
   * voxel family with the full shaping vocabulary). */
  massFamily?: string;
}

/** A mass family: what an island's body is BUILT from. The cliff family
 * keeps the whole nature shaping vocabulary (wedges, trims, caves,
 * waterfalls, strata); "block" families are simpler voxel stacks with
 * INTEGRATED tops (no cap tile) — measured: Platformer blocks are clean
 * 1.0 full / 0.5 low courses, bottom-center, with a cosmetic 0.082 lip
 * overhang that reads as authored trim. Families without quarter courses
 * bottom out up to 0.45 shallow — the underside faces the void.
 * RESIDUAL: block-*-corner rim rounding awaits a mesh-orientation
 * measure (bbox alone cannot orient the rounded edge). */
interface MassFamily {
  kind: "cliff" | "block";
  block?: string;
  half?: string;
}

const MASS_FAMILIES: Record<string, MassFamily> = {
  cliff: { kind: "cliff" },
  blockGrass: {
    kind: "block",
    block: "platformer-kit:block-grass",
    half: "platformer-kit:block-grass-low",
  },
  blockSnow: {
    kind: "block",
    block: "platformer-kit:block-snow",
    half: "platformer-kit:block-snow-low",
  },
};

/** Island families. Emergent variety comes from (biome × size × run shape ×
 * depth noise); new packs slot in as new rows. */
export const BIOMES: readonly BiomeSpec[] = [
  {
    name: "forest",
    weight: 3,
    material: "stone",
    capId: "nature-kit:ground_grass",
    capChance: 0.9,
    setting: "forest",
    decorDensity: 0.65,
  },
  {
    name: "meadow",
    weight: 2.5,
    material: "stone",
    capId: "nature-kit:ground_grass",
    capChance: 0.95,
    setting: "meadow",
    decorDensity: 0.55,
  },
  {
    name: "rocky",
    weight: 2.5,
    material: "rock",
    capId: null,
    capChance: 0,
    setting: "rocky",
    decorDensity: 0.4,
  },
  {
    name: "barren",
    weight: 1.5,
    material: "rock",
    capId: null,
    capChance: 0,
    setting: "rocky",
    decorDensity: 0.15,
  },
  // Rare novelty: dev-map island — bare geometry in TF2-orange/grey blocking
  // colors (the renderer swaps its color scheme; no caps, no vegetation).
  {
    name: "abstract",
    weight: 0.6,
    material: "rock",
    capId: null,
    capChance: 0,
    setting: null,
    decorDensity: 0,
  },
  // M18a themed rows (Holiday/Graveyard/Pirate/Survival kits). Weights
  // sized so the nature families keep ~70% of draws — a themed island is
  // an EVENT (liveliness = distribution over a long stretch, not
  // density). Caps reuse the 1×1 nature ground tile; the client tints it
  // per biome (snow / sand). Landmark scales normalize pack sizing
  // (pirate ~2.5-3x nature, survival props tiny) — measured 2026-08-09.
  {
    name: "winter",
    weight: 1.2,
    material: "stone",
    // Mass-family round: winter islands are BUILT of snow now (Platformer
    // block-snow voxels, integrated snow top) — the tinted-cliff + white
    // cap phenotype retired.
    capId: null,
    capChance: 0,
    setting: "winter",
    decorDensity: 0.5,
    landmarks: [
      "holiday-kit:snowman",
      "holiday-kit:snowman-hat",
      "holiday-kit:tree-decorated",
      "holiday-kit:nutcracker",
    ],
    landmarkScale: 1.1,
    massFamily: "blockSnow",
  },
  {
    name: "spooky",
    weight: 0.8,
    material: "rock",
    capId: null,
    capChance: 0,
    setting: "spooky",
    decorDensity: 0.55,
    landmarks: [
      "graveyard-kit:crypt-small",
      "graveyard-kit:crypt",
      "graveyard-kit:pillar-obelisk",
      "graveyard-kit:cross-column",
      "graveyard-kit:lightpost-all",
    ],
    landmarkScale: 1.2,
  },
  {
    name: "tropical",
    weight: 1.2,
    material: "stone",
    capId: "nature-kit:ground_grass",
    capChance: 0.85,
    setting: "tropical",
    decorDensity: 0.5,
    landmarks: [
      "pirate-kit:flag-pirate-high",
      "pirate-kit:cannon-mobile",
      "pirate-kit:tower-complete-small",
    ],
    landmarkScale: 0.35,
  },
  {
    name: "autumn",
    weight: 1.0,
    material: "rock",
    capId: null,
    capChance: 0,
    setting: "autumn",
    decorDensity: 0.55,
    landmarks: [
      "survival-kit:tent",
      "survival-kit:structure-canvas",
      "survival-kit:signpost",
      "survival-kit:campfire-pit",
      "survival-kit:workbench-anvil",
    ],
    landmarkScale: 1.5,
  },
  // Mass-family round: a whole-different island BODY — chunky grass
  // voxels with the platformer lip, integrated green top.
  {
    name: "grove",
    weight: 1.0,
    material: "stone",
    capId: null,
    capChance: 0,
    setting: "forest",
    decorDensity: 0.6,
    massFamily: "blockGrass",
  },
];

export interface Island {
  id: number;
  ref: string;
  biome: string;
  /** Top surface height in WORLD units (track topY × TRACK_SCALE). */
  topY: number;
  /** Placements in WORLD coordinates (island blocks/decor are 1× scale —
   * the slalom rule: track ×2, world 1×). */
  placements: Placement[];
  /** Top-surface mass cells (world-grid "x,z"), the run patch included. */
  cells: string[];
}

const NATURE = "nature-kit";
const key = (x: number, z: number) => `${x},${z}`;

/** Default landmark vocabulary (fully-qualified) — the historical nature
 * statue/rock list, byte-identical draws for the pre-M18a biome rows. */
const DEFAULT_LANDMARKS: readonly string[] = [
  "nature-kit:statue_head",
  "nature-kit:statue_obelisk",
  "nature-kit:statue_column",
  "nature-kit:statue_columnDamaged",
  "nature-kit:statue_ring",
  "nature-kit:rock_tallA",
  "nature-kit:rock_tallD",
  "nature-kit:stone_tallB",
];

export function buildIsland(
  rng: Rng,
  crossing: IslandCrossing,
  runCells: readonly (readonly [number, number])[],
  occ: Occupancy,
  style: StyleField,
  catalog: DecorCatalog,
): Island {
  const { id, topY, margin } = crossing;
  const worldTop = topY * TRACK_SCALE;
  const ref = `isle/${id}`;
  // Terrace top per TRACK cell — the EXACT rule the planner claimed with
  // (nearest run cell's floor base, Chebyshev, ties pick the lower top).
  // Mass claims are recognized by their y1 matching this; recomputing
  // with any other rule would orphan claims at tie cells.
  const profile = crossing.profile;
  const terraceTop = (cx: number, cz: number): number => {
    let best = Infinity;
    let top = topY;
    for (let j = 0; j < runCells.length; j++) {
      const [rx, rz] = runCells[j];
      const d = Math.max(Math.abs(cx - rx), Math.abs(cz - rz));
      if (d < best || (d === best && profile[j] < top)) {
        best = d;
        top = profile[j];
      }
    }
    return top;
  };
  // M24: the seed's temperament leans the island draw — a serene seed
  // grows meadows, a treacherous one spooky crags.
  const biome = rng.weighted(
    BIOMES.map((b) =>
      [b, b.weight * (style.temperament.biomeBias[b.name] ?? 1)] as const
    ),
  );
  const family = MASS_FAMILIES[biome.massFamily ?? "cliff"] ??
    MASS_FAMILIES.cliff;
  const placements: Placement[] = [];

  // The run's WORLD patch: each track cell covers a 2×2 set of world cells
  // (slalom rule — the channel swath the mass must carry). A diamond's
  // branch columns are track too: full mass beneath, no caps, no decor.
  const runWorld = new Set<string>();
  for (const [cx, cz] of runCells) {
    for (const [wx, wz] of worldCellsOf(cx, cz)) runWorld.add(key(wx, wz));
  }
  for (const [cx, cz] of crossing.branchCells ?? []) {
    for (const [wx, wz] of worldCellsOf(cx, cz)) runWorld.add(key(wx, wz));
  }
  // Wide-run flanks and furniture ledges are track patch too: full mass
  // beneath, no caps (a cap tile would z-fight the channel/cap pieces
  // standing there), no decor.
  for (const [cx, cz] of crossing.wideFlanks ?? []) {
    for (const [wx, wz] of worldCellsOf(cx, cz)) runWorld.add(key(wx, wz));
  }
  for (const { cell } of crossing.sideCells ?? []) {
    for (const [wx, wz] of worldCellsOf(cell[0], cell[1])) {
      runWorld.add(key(wx, wz));
    }
  }
  const runWorldList = [...runWorld].map((k) => k.split(",").map(Number));
  const chebToRun = (x: number, z: number): number => {
    let best = Infinity;
    for (const [rx, rz] of runWorldList) {
      best = Math.min(best, Math.max(Math.abs(x - rx), Math.abs(z - rz)));
    }
    return best;
  };

  // ---------------------------------------------------------------------
  // Mass columns on the WORLD grid: run columns always; ring columns thin
  // and shallow with distance → stepped inverted-ziggurat underside. All
  // decisions per-world-cell-keyed (order-independent).
  // ---------------------------------------------------------------------
  const keepFor = (dist: number): number =>
    dist <= 1 ? 1 : Math.max(0.2, 1.05 - dist * 0.16);
  const topCells: string[] = [];

  // PASS 1 — column existence + depth per world cell (all per-cell-keyed
  // draws, order-independent). Shaping needs neighbor knowledge, so
  // emission is a second pass.
  interface Col {
    wx: number;
    wz: number;
    tx: number;
    tz: number;
    topWorld: number;
    depth: number;
    /** Claim bottom (world) — the column was clipped here if an older
     * band sits directly beneath. */
    clipY: number;
    clipped: boolean;
    onRun: boolean;
    dist: number;
  }
  const cols = new Map<string, Col>();
  for (const claim of occ.claimsOf(ref)) {
    const { x, z, y0, y1 } = claim;
    const cellTop = terraceTop(x, z);
    if (Math.abs(y1 - cellTop) > 1e-9) continue; // canopy claim, not mass
    const cellTopWorld = cellTop * TRACK_SCALE;
    const availWorld = (cellTop - y0) * TRACK_SCALE;
    if (availWorld < 0.999) continue;
    for (const [wx, wz] of worldCellsOf(x, z)) {
      const cellRng = rng.child(`cell/${wx}/${wz}`);
      const dist = chebToRun(wx, wz);
      const onRun = runWorld.has(key(wx, wz));
      if (!onRun && !cellRng.chance(keepFor(dist))) continue;

      // Depth target: deepest under the run, shallowing outward, jittered.
      const target = Math.max(
        1,
        (margin * TRACK_SCALE + 2.5 - dist) * (0.5 + cellRng.float() * 0.45),
      );
      const depth = quantize(Math.min(availWorld, target));
      cols.set(key(wx, wz), {
        wx,
        wz,
        tx: x,
        tz: z,
        topWorld: cellTopWorld,
        depth,
        clipY: y0 * TRACK_SCALE,
        clipped: depth >= availWorld - 1e-9 && availWorld < target,
        onRun,
        dist,
      });
    }
  }

  // ISLAND SHAPING (backlog): trims, cave mouths, waterfalls, landmarks.
  // A shoulder cell is a non-run top with at least one open orthogonal
  // side at its own level; single-open sides slope down and out, double-
  // open corners take the diagonal cut. Old-track piercings get a cave
  // mouth facing along the pierced channel; one edge column per some
  // islands runs a waterfall down its face; big islands may carry one
  // landmark. Shaped tops are excluded from caps/decor (their surface
  // is no longer flat).
  const DIRS: readonly (readonly [number, number])[] = [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
  ];
  /** How far the surface DROPS from this column's top to the neighbor
   * (Infinity into the void). Shaping decisions read the surface through
   * this: a wedge face needs a full course of air or it sinks into the
   * neighbor's block. */
  const dropAt = (c: Col, dx: number, dz: number): number => {
    const n = cols.get(key(c.wx + dx, c.wz + dz));
    return n ? c.topWorld - n.topWorld : Infinity;
  };
  /** Open at the column's own level: no column there, or one whose top
   * sits a FULL course or more lower (USER RULE: pyramid/wedge blocks
   * must respect the surface they are on — a side counted "open" at a
   * half-course drop buried the wedge's low edge in its neighbor). */
  const openAt = (c: Col, dx: number, dz: number): boolean =>
    dropAt(c, dx, dz) >= 0.99;
  const shaped = new Set<string>(); // no caps/decor on these tops

  // Shoulder trim is an ISLAND-level trait (WFC coherence rule, USER RULE:
  // triangles must never read jagged): either the whole rim bevels or the
  // island stays blocky. The old per-cell 0.7 coin left random full cubes
  // standing inside bevel runs — a sawtooth silhouette.
  const trimIsland = rng.child("trim").chance(0.75);

  // Waterfall pick (per island): one qualifying edge column.
  let waterfallCell: string | null = null;
  {
    const wRng = rng.child("waterfall");
    if (wRng.chance(0.4)) {
      const cands = [...cols.values()]
        .filter((c) =>
          !c.onRun && c.dist >= 1 && c.depth >= 2 &&
          DIRS.some(([dx, dz]) => openAt(c, dx, dz))
        )
        .map((c) => key(c.wx, c.wz))
        .sort();
      if (cands.length > 0) waterfallCell = wRng.pick(cands);
    }
  }
  // Landmark pick (per island, larger islands lean in). Biome rows may
  // carry their own vocabulary; the default is the nature statue list
  // (same length + order as ever — the pick index is stream-identical).
  let landmarkCell: string | null = null;
  const LANDMARKS = biome.landmarks ?? DEFAULT_LANDMARKS;
  {
    const lRng = rng.child("landmark");
    if (lRng.chance(margin >= 2 ? 0.35 : 0.15)) {
      const cands = [...cols.values()]
        .filter((c) => {
          if (c.onRun || c.dist < 2) return false;
          // Needs its own airspace (canopy claim at the cell's terrace).
          const top = c.topWorld / TRACK_SCALE;
          return occ.at(c.tx, c.tz).some((b) =>
            b.ref === ref && b.y1 > top + 0.5
          );
        })
        .map((c) => key(c.wx, c.wz))
        .sort();
      if (cands.length > 0) landmarkCell = lRng.pick(cands);
    }
  }

  // Per-island deep-course material swap (varied texturing): some
  // islands sit on a base of the OTHER cliff material.
  const otherMat = biome.material === "rock" ? "stone" : "rock";
  const deepMat = rng.child("strata").chance(0.4) ? otherMat : biome.material;
  const matAt = (yTop: number, topWorld: number): string =>
    yTop <= topWorld - 2 + 1e-9 ? deepMat : biome.material;

  /** Rotation turning local −z toward world direction (dx, dz) — the
   * cave arch and the waterfall face live on −z (measured in gallery). */
  const faceRot = (dx: number, dz: number): Placement["rotation"] =>
    (dx === 1 ? 3 : dx === -1 ? 1 : dz === -1 ? 0 : 2) as Placement[
      "rotation"
    ];
  const dirOf = (dx: number, dz: number): number =>
    dx === 1 ? 0 : dz === 1 ? 1 : dx === -1 ? 2 : 3;
  /** cliff_blockSlope descends toward local +x (measured): rotation
   * mapping local +x onto world dir w. */
  const slopeRot = (w: number): Placement["rotation"] =>
    ((4 - w) % 4) as Placement["rotation"];
  /** cliff_blockDiagonal's cut corner spans local +x/+z (measured):
   * rotation mapping that corner onto adjacent open dirs (k, k+1). */
  const diagRot = (k: number): Placement["rotation"] =>
    ((4 - k) % 4) as Placement["rotation"];

  /** The slope direction a column would wedge toward under the
   * flush-or-open law, from tops alone (null = keeps the cube; corner
   * diagonals also report null). Pure in `cols`, so a cell can ask it of
   * a NEIGHBOR: the tops-only law is blind to the neighbor's top-block
   * SHAPE, and two same-height side-by-side wedges sloping OPPOSITE ways
   * would each treat the other's sloping side as an anchor — leaving a
   * bowtie of exposed triangle slivers where the faces don't overlap. */
  const wedgeDirOf = (col: Col | undefined): number | null => {
    if (!col || col.onRun || col.dist < 1 || col.depth < 1) return null;
    let openDir = -1;
    for (let w = 0; w < 4; w++) {
      const d = dropAt(col, DIRS[w][0], DIRS[w][1]);
      if (d > 0.01 && d < 0.99) return null;
      if (d >= 0.99) {
        if (openDir >= 0) return null;
        openDir = w;
      }
    }
    return openDir >= 0 ? openDir : null;
  };

  // PASS 1.5 — RIM-RUN COHERENCE (user round 2 of the triangle blocks):
  // per-cell wedge eligibility flickers along a straight rim (half-step
  // neighbors, protrusion corners, depth dips), and a rim that mixes
  // chamfered and square cells reads as a sawtooth comb — the wedge law
  // kept every piece well-mated yet ENFORCED the jagged silhouette.
  // A slope is now only allowed inside a contiguous run of ≥3 cells all
  // eligible toward the SAME open side: long rims read as one authored
  // bevel, noisy rims keep calm cubes. Diagonals only cap a committed
  // run.
  const slopeEligible = new Map<string, number>();
  for (const c of cols.values()) {
    const k = key(c.wx, c.wz);
    if (k === waterfallCell || c.onRun || c.dist < 1 || c.depth < 1) continue;
    const open = DIRS.filter(([dx, dz]) => openAt(c, dx, dz));
    if (open.length !== 1) continue;
    const flush = DIRS.every(([dx, dz]) => {
      const d = dropAt(c, dx, dz);
      return d <= 0.01 || d >= 0.99;
    });
    if (!flush) continue;
    const w = dirOf(open[0][0], open[0][1]);
    const sideMated = [1, 3].every((t) => {
      const s = (w + t) % 4;
      const n = cols.get(key(c.wx + DIRS[s][0], c.wz + DIRS[s][1]));
      if (!n || Math.abs(n.topWorld - c.topWorld) > 0.01) return true;
      return wedgeDirOf(n) !== (w + 2) % 4;
    });
    if (sideMated) slopeEligible.set(k, w);
  }
  const trimKeep = new Set<string>();
  for (const [k, w] of slopeEligible) {
    const [x, z] = k.split(",").map(Number);
    const [px, pz] = w % 2 === 0 ? [0, 1] : [1, 0]; // along the rim
    let run = 1;
    for (const s of [1, -1]) {
      let n = 1;
      while (slopeEligible.get(key(x + px * n * s, z + pz * n * s)) === w) {
        run++;
        n++;
      }
    }
    if (run >= 3) trimKeep.add(k);
  }
  const nearTrimRun = (wx: number, wz: number): boolean =>
    DIRS.some(([dx, dz]) => trimKeep.has(key(wx + dx, wz + dz)));

  // PASS 2 — emit blocks with shaping.
  for (const c of cols.values()) {
    const cellRng = rng.child(`blocks/${c.wx}/${c.wz}`);
    const k = key(c.wx, c.wz);
    const open = DIRS.filter(([dx, dz]) => openAt(c, dx, dz));

    // Waterfall column: waterfallTop at the crest, waterfall faces below.
    const isFall = k === waterfallCell && open.length > 0;
    // Shoulder trim on plain edge tops (never on the waterfall). WFC
    // wedge law (USER RULE: triangles must never read jagged): every side
    // of a wedge must be either FLUSH (neighbor top at the wedge's own
    // level or higher) or fully OPEN (a clean full-course drop). Any
    // in-between step — the kit's 0.5/0.25 half courses — disqualifies
    // the cell: half-step terraces otherwise grow staircases of exposed
    // triangle profiles. A slope then needs exactly ONE open side; a
    // diagonal cut exactly two ADJACENT open sides with the corner
    // between them open too (else the cut face jams into that corner).
    const flush = DIRS.every(([dx, dz]) => {
      const d = dropAt(c, dx, dz);
      return d <= 0.01 || d >= 0.99;
    });
    const adjacentOpen = open.length === 2 &&
      open[0][0] !== open[1][0] && open[0][1] !== open[1][1];
    const dirs = open.map(([dx, dz]) => dirOf(dx, dz));
    const corner = adjacentOpen &&
      dropAt(c, open[0][0] + open[1][0], open[0][1] + open[1][1]) >= 0.99;
    // Side-mating: a slope's side triangles only mate with a same-height
    // neighbor that is a cube, a wedge sloping the SAME way, or a wedge
    // sloping perpendicular-away (whose facing side is its full back).
    // An opposite-sloping same-height pair is forbidden (both cells keep
    // cubes — the check is symmetric).
    const sideMated = open.length !== 1 ||
      [1, 3].every((t) => {
        const w = dirs[0];
        const s = (w + t) % 4;
        const n = cols.get(key(c.wx + DIRS[s][0], c.wz + DIRS[s][1]));
        if (!n || Math.abs(n.topWorld - c.topWorld) > 0.01) return true;
        return wedgeDirOf(n) !== (w + 2) % 4;
      });
    const trim = trimIsland && !isFall && !c.onRun && c.dist >= 1 &&
      c.depth >= 1 && flush && sideMated &&
      (corner
        // A diagonal cut only CAPS a committed bevel run — a lone corner
        // notch on an untrimmed rim reads as a random bite.
        ? nearTrimRun(c.wx, c.wz)
        : open.length === 1 && trimKeep.has(k));

    // BLOCK mass families (user directive): a simpler voxel body — full
    // + half courses only, integrated top, no cliff shaping. The cliff
    // path below is untouched (byte-identical for cliff biomes).
    if (family.kind === "block") {
      let remB = c.depth;
      let yB = c.topWorld;
      while (remB >= 1 - 1e-9) {
        placements.push({
          pieceId: family.block!,
          pos: [c.wx, yB - 1, c.wz],
          rotation: cellRng.int(0, 4) as Placement["rotation"],
          role: "support",
        });
        yB -= 1;
        remB -= 1;
      }
      if (remB >= 0.5 - 1e-9 && family.half) {
        placements.push({
          pieceId: family.half,
          pos: [c.wx, yB - 0.5, c.wz],
          rotation: cellRng.int(0, 4) as Placement["rotation"],
          role: "support",
        });
      }
      topCells.push(k);
      if (k === landmarkCell && !shaped.has(k)) {
        placements.push({
          pieceId: rng.child(`landmark/${k}`).pick(LANDMARKS as string[]),
          pos: [c.wx, c.topWorld, c.wz],
          rotation: cellRng.int(0, 4) as Placement["rotation"],
          role: "decor",
          ...(biome.landmarkScale !== undefined
            ? { scale: biome.landmarkScale }
            : {}),
        });
        shaped.add(k);
      }
      continue;
    }

    // One face per waterfall column — water can't zigzag around the
    // corner course to course (the old per-course re-pick did).
    const fallFace = isFall ? cellRng.pick(open) : null;

    let remaining = c.depth;
    let yTop = c.topWorld;
    let course = 0;
    while (remaining >= 1 - 1e-9) {
      const mat = matAt(yTop, c.topWorld);
      if (course === 0 && isFall) {
        const [dx, dz] = fallFace!;
        placements.push({
          pieceId: `${NATURE}:cliff_waterfallTop_${mat}`,
          pos: [c.wx, yTop - 1, c.wz],
          rotation: faceRot(dx, dz),
          role: "support",
        });
        shaped.add(k);
      } else if (course > 0 && isFall && remaining >= 2 - 1e-9) {
        // Water continues down the face toward the void.
        const [dx, dz] = fallFace!;
        placements.push({
          pieceId: `${NATURE}:cliff_waterfall_${mat}`,
          pos: [c.wx, yTop - 1, c.wz],
          rotation: faceRot(dx, dz),
          role: "support",
        });
      } else if (course === 0 && trim) {
        // Slope toward the single open side; diagonal across a corner
        // (the cut needs the cell BETWEEN its two open sides open too,
        // else the cut face jams into that block's corner).
        placements.push({
          pieceId: `${NATURE}:${
            corner ? "cliff_blockDiagonal" : "cliff_blockSlope"
          }_${mat}`,
          pos: [c.wx, yTop - 1, c.wz],
          rotation: corner
            // Anchor the cut on the dir whose successor is also open.
            ? diagRot(
              dirs.includes((dirs[0] + 1) % 4) ? dirs[0] : dirs[1],
            )
            : slopeRot(dirs[0]),
          role: "support",
        });
        shaped.add(k);
      } else if (
        remaining < 2 - 1e-9 && c.clipped &&
        Math.abs(yTop - 1 - c.clipY) < 0.26
      ) {
        // CAVE MOUTH: the column bottoms out on an older channel band —
        // the mass was pierced. Face the arch along the pierced line
        // (the neighbor track cell carrying a band at the same height).
        const below = occ.at(c.tx, c.tz).find((b) =>
          b.ref !== ref && Math.abs(b.y1 * TRACK_SCALE - c.clipY) < 0.05
        );
        let arch: readonly [number, number] | null = null;
        if (below) {
          for (const [dx, dz] of DIRS) {
            if (
              occ.at(c.tx + dx, c.tz + dz).some((b) =>
                b.ref === below.ref &&
                Math.abs(b.y1 - below.y1) < 0.15
              )
            ) {
              arch = [dx, dz];
              break;
            }
          }
        }
        placements.push(
          arch
            ? {
              pieceId: `${NATURE}:cliff_blockCave_${matAt(yTop, c.topWorld)}`,
              pos: [c.wx, yTop - 1, c.wz],
              rotation: faceRot(arch[0], arch[1]),
              role: "support",
            }
            : block(
              `cliff_block_${matAt(yTop, c.topWorld)}`,
              c.wx,
              yTop - 1,
              c.wz,
              cellRng,
            ),
        );
      } else {
        placements.push(
          block(`cliff_block_${mat}`, c.wx, yTop - 1, c.wz, cellRng),
        );
      }
      yTop -= 1;
      remaining -= 1;
      course++;
    }
    if (remaining >= 0.5 - 1e-9) {
      placements.push(
        block(
          `cliff_blockHalf_${matAt(yTop, c.topWorld)}`,
          c.wx,
          yTop - 0.5,
          c.wz,
          cellRng,
        ),
      );
      yTop -= 0.5;
      remaining -= 0.5;
    }
    if (remaining >= 0.25 - 1e-9) {
      placements.push(
        block(
          `cliff_blockQuarter_${matAt(yTop, c.topWorld)}`,
          c.wx,
          yTop - 0.25,
          c.wz,
          cellRng,
        ),
      );
    }
    topCells.push(k);

    // Landmark decor stands alone on its cell.
    if (k === landmarkCell && !shaped.has(k)) {
      placements.push({
        pieceId: rng.child(`landmark/${k}`).pick(LANDMARKS as string[]),
        pos: [c.wx, c.topWorld, c.wz],
        rotation: cellRng.int(0, 4) as Placement["rotation"],
        role: "decor",
        ...(biome.landmarkScale !== undefined
          ? { scale: biome.landmarkScale }
          : {}),
      });
      shaped.add(k);
    }

    // Caps: biome surface tile on non-track FLAT tops. Raised viaduct
    // runs (crossing.raised) cap their deck too — the track stands on
    // piers 0.5/1.0 ABOVE it, so the tile is visible and can't z-fight
    // the channel.
    if (
      (!c.onRun || (crossing.raised ?? 0) > 0) && !shaped.has(k) &&
      biome.capId &&
      cellRng.chance(biome.capChance)
    ) {
      placements.push({
        pieceId: biome.capId,
        pos: [c.wx, c.topWorld, c.wz],
        rotation: cellRng.int(0, 4) as Placement["rotation"],
        role: "decor",
      });
    }
  }

  // ---------------------------------------------------------------------
  // Decor field on the top surface (world grid): the M2a API with the run
  // patch as "the track", filtered to actual mass tops, lifted to the top.
  // ---------------------------------------------------------------------
  if (biome.setting && catalog.packs.length > 0) {
    const setting = settingByName(biome.setting, catalog);
    const topSet = new Set(topCells);
    const decor = placeDecor(
      rng.child("decor"),
      setting,
      { ...style, decorDensity: biome.decorDensity },
      regionAround(runWorld, margin * TRACK_SCALE),
      cellSetOracle(runWorld),
      catalog,
    );
    for (const p of decor) {
      const cx = Math.round(p.pos[0] - 0.5) + 0.5;
      const cz = Math.round(p.pos[2] - 0.5) + 0.5;
      if (!topSet.has(key(cx, cz)) || runWorld.has(key(cx, cz))) continue;
      // Shaped tops (trims, waterfalls, landmarks) keep their silhouette.
      if (shaped.has(key(cx, cz))) continue;
      // Vegetation needs the canopy airspace claim (made at plan time in
      // TRACK cells) — cells whose air an older line owns stay bare rather
      // than growing a tree through someone's channel. Decor sits on the
      // cell's own terrace.
      const tx = Math.round(cx / TRACK_SCALE);
      const tz = Math.round(cz / TRACK_SCALE);
      const cellTop = terraceTop(tx, tz);
      const ownsAir = occ
        .at(tx, tz)
        .some((b) => b.ref === ref && b.y1 > cellTop + 0.5);
      if (!ownsAir) continue;
      placements.push({
        ...p,
        pos: [p.pos[0], cellTop * TRACK_SCALE, p.pos[2]],
      });
    }
  }

  return {
    id,
    ref,
    biome: biome.name,
    topY: worldTop,
    placements,
    cells: topCells,
  };
}

function quantize(depth: number): number {
  return Math.max(1, Math.round(depth * 4) / 4);
}

function block(
  pieceId: string,
  x: number,
  y: number,
  z: number,
  rng: Rng,
): Placement {
  return {
    pieceId: `${NATURE}:${pieceId}`,
    pos: [x, y, z],
    // Random facing — the cliff faces are textured on all sides.
    rotation: rng.int(0, 4) as Placement["rotation"],
    role: "support",
  };
}
